import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import HasPermission from "src/core/guards/has-permission";
import {
  getBlockStatusByBlockCode,
  getLocationBlockByCode,
} from "../../../../core/api/services/dashbaorad-details/location-block.services";
import BreadcrumbComponent from "../../../../core/components/breadcrumb/breadcrumb.component";
import ContentHeader from "../../../../shared/content-header/content-header.component";
import SkeletonComponent from "../../../../shared/custom-spinner/skeleton.component";
import { safeAtob } from "../../../../shared/helpers/enode-decode-string";
import { BreadCrumbInfoModel } from "../../../../shared/models/breadCrumbInfo.model";
import TitleComponent from "../../../../shared/title/title.component";
import DashboardDetailBlockStatus from "./dashboard-blockdetails-status/dashboard-detail-block-status";
import DashboardDetailMapWithBlockInfo from "./dashboard-blockdetails-status/dashboard-detail-map-with-block-info";
import BurnTimeChart from "./dashboard-charts/burn-time-chart";
import BurnTimeMaintenanceChart from "./dashboard-charts/burn-time-maintenance-chart";
import EnergyConsumptionChart from "./dashboard-charts/energy-consumption-chart";
import MotionHeatmapChart from "./dashboard-charts/motion-heatmap-chart";
import MotionTrafficChart from "./dashboard-charts/motion-traffic-chart";
import DashboardGenericActionsComponent from "./dashboard-generic-actions/dashboard-generic-actions.component";

const DashboardDetailsComponent = () => {
  const location = useLocation();
  const params: any = useParams();
  const blockCode = params.blockCode ? safeAtob(params.blockCode) : null;

  const navigate = useNavigate();

  const navigateTo = (url: string) => {
    if (url) navigate(url);
  };

  const [isRefreshedLocationBlock, setIsRefreshedLocationBlock] =
    useState<boolean>(false);
  const [isRefreshedLuminaryStatus, setIsRefreshedLuminaryStatus] =
    useState<boolean>(false);
  const [isRefreshedHealthStatus, setIsRefreshedHealthStatus] =
    useState<boolean>(false);
  const [isRefreshedEquipmentQuantity, setIsRefreshedEquipmentQuantity] =
    useState<boolean>(false);
  const [isRefreshedMaintenance, setIsRefreshedMaintenance] =
    useState<boolean>(false);
  const [isRefreshedMotionTraffic, setIsRefreshedMotionTraffic] =
    useState<boolean>(false);
  const [isRefreshedMotionHeatmap, setIsRefreshedMotionHeatmap] =
    useState<boolean>(false);
  const [isRefreshedBurnChart, setIsRefreshedBurnChart] =
    useState<boolean>(false);
  const [isRefreshedEnergyConsumption, setIsRefreshedEnergyConsumption] =
    useState<boolean>(false);

  const queryClient = useQueryClient();

  const [breadCrumbInfo, setBreadCrumbInfo] = useState<BreadCrumbInfoModel[]>(
    []
  );

  const { data: blockDetail, isLoading: isLoadingBlockDetails } = useQuery({
    queryKey: ["getLocationBlockByCode", blockCode],
    queryFn: ({ signal }) => getLocationBlockByCode(blockCode, signal),
    enabled: !!blockCode,
  });

  const { data: blockStatus } = useQuery({
    queryKey: ["blockStatus", blockCode],
    queryFn: ({ signal }) => getBlockStatusByBlockCode(blockCode, signal),
    enabled: !!blockCode,
  });

  const [blockDetailData, setBlockDetailData] = useState([] as any);
  const [faultNavigateData, setFaultNavigateData] = useState({
    blockCode: "",
    siteCode: "",
    tcCode: "",
  });

  useEffect(() => {
    if (blockDetail?.blockName) {
      setBreadCrumbInfo([
        {
          label: "Dashboard",
          active: false,
          onClick: () => navigateTo("/dashboard"),
        },
        {
          label: `${blockDetail?.blockName}, ${blockDetail?.street}, ${blockDetail?.siteName}, ${blockDetail?.blockCode}`,
          active: true,
        },
      ]);
    } else {
      setBreadCrumbInfo([
        {
          label: "Dashboard",
          active: false,
          onClick: () => navigateTo("/dashboard"),
        },
      ]);
    }

    setBlockDetailData([
      {
        title: "Town Council",
        content: blockDetail?.tcName,
      },
      {
        title: "Site",
        content: blockDetail?.siteName,
      },
      {
        title: "Block",
        content: blockDetail?.blockName,
      },
    ]);
    setFaultNavigateData({
      blockCode: blockDetail?.blockCode,
      siteCode: blockDetail?.siteCode,
      tcCode: blockDetail?.tcCode,
    });
  }, [blockDetail]);

  const onRefetch = async () => {
    setIsRefreshedLocationBlock(true);
    setIsRefreshedLuminaryStatus(true);
    setIsRefreshedHealthStatus(true);
    setIsRefreshedEquipmentQuantity(true);
    setIsRefreshedMaintenance(true);
    setIsRefreshedMotionTraffic(true);
    setIsRefreshedMotionHeatmap(true);
    setIsRefreshedBurnChart(true);
    setIsRefreshedEnergyConsumption(true);

    await queryClient.invalidateQueries({
      queryKey: ["getLocationBlockByCode"],
    });
    setIsRefreshedLocationBlock(false);

    await queryClient.invalidateQueries({
      queryKey: ["getLuminaryStatusByBlockCode"],
    });
    setIsRefreshedLuminaryStatus(false);

    await queryClient.invalidateQueries({
      queryKey: ["getHealthStatusByLocationOrAssetCode"],
    });
    setIsRefreshedHealthStatus(false);

    await queryClient.invalidateQueries({
      queryKey: ["getEquipmentQuantityByBlockCode"],
    });
    setIsRefreshedEquipmentQuantity(false);

    await queryClient.invalidateQueries({
      queryKey: ["getMaintenanceData"],
    });
    setIsRefreshedMaintenance(false);

    await queryClient.invalidateQueries({
      queryKey: ["getMotionTrafficData"],
    });
    setIsRefreshedMotionTraffic(false);

    await queryClient.invalidateQueries({
      queryKey: ["getMotionHeatmapData"],
    });
    setIsRefreshedMotionHeatmap(false);

    await queryClient.invalidateQueries({
      queryKey: ["getBurnChartData"],
    });
    setIsRefreshedBurnChart(false);

    await queryClient.invalidateQueries({
      queryKey: ["getEnergyConsumptionData"],
    });
    setIsRefreshedEnergyConsumption(false);

    await queryClient.invalidateQueries({
      queryKey: ["getBaselineListByModuleId"],
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Detect page refresh/closing
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem("postRefreshRedirect", location.pathname);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [location.pathname]);

  return (
    <>
      <Helmet>
        <title>
          {blockDetail?.blockName
            ? `${blockDetail?.blockName}, ${blockDetail?.street}, ${blockDetail?.siteName}, ${blockDetail?.blockCode}
          | Dashboard `
            : "Block Details | Dashboard"}
        </title>
      </Helmet>
      <div className="header-container">
        <div className="title-container">
          <SkeletonComponent
            wrapperHeight="90px"
            skeletonHeight="50px"
            isLoading={isLoadingBlockDetails || false}
          >
            {blockDetail && Object.keys(blockDetail).length ? (
              <ContentHeader
                pageTitle={`${blockDetail?.blockName}, ${blockDetail?.street}, ${blockDetail?.siteName}, ${blockDetail?.blockCode}`}
                isVerified={blockStatus?.isVerified}
                isBlockDetails={true}
              />
            ) : null}
          </SkeletonComponent>
        </div>
      </div>

      <div className="page">
        <div className="page-container mt-3">
          <SkeletonComponent
            wrapperHeight="30px"
            skeletonHeight="20px"
            isLoading={isLoadingBlockDetails}
          >
            <BreadcrumbComponent breadCrumbInfo={breadCrumbInfo} />
          </SkeletonComponent>

          <DashboardGenericActionsComponent onRefetch={onRefetch} />

          <div>
            <div className="mb-4">
              <TitleComponent title="Block Details And Status" />
            </div>

            <div className="flex justify-content-between gap-4 mt-3">
              <DashboardDetailMapWithBlockInfo
                blockDetail={blockDetail}
                blockDetailData={blockDetailData}
                isLoadingBlockDetails={isLoadingBlockDetails}
                isRefreshedLocationBlock={isRefreshedLocationBlock}
              />
              <HasPermission
                moduleName="Dashboard Detail"
                isRoute={false}
                actionName="View-Dashboard Detail"
              >
                <DashboardDetailBlockStatus
                  blockCode={blockCode}
                  faultNavigateData={faultNavigateData}
                  isLoadingBlockDetails={isLoadingBlockDetails}
                  isRefreshedEquipmentQuantity={isRefreshedEquipmentQuantity}
                  isRefreshedLuminaryStatus={isRefreshedLuminaryStatus}
                  isRefreshedHealthStatus={isRefreshedHealthStatus}
                />
              </HasPermission>
            </div>
          </div>

          {/* charts */}
          <div>
            <div className="mb-4 mt-4">
              <TitleComponent title="Analytics" />
            </div>

            <BurnTimeMaintenanceChart
              blockCode={blockCode}
              isRefreshed={isRefreshedMaintenance}
            />

            <HasPermission
              moduleName="Dashboard Detail"
              isRoute={false}
              actionName="View-Dashboard Detail"
            >
              <MotionTrafficChart
                blockCode={blockCode}
                isRefreshed={isRefreshedMotionTraffic}
              />
            </HasPermission>

            <HasPermission
              moduleName="Dashboard Detail"
              isRoute={false}
              actionName="View-Dashboard Detail"
            >
              <MotionHeatmapChart
                blockCode={blockCode}
                isRefreshed={isRefreshedMotionHeatmap}
              />
            </HasPermission>

            <HasPermission
              moduleName="Dashboard Detail"
              isRoute={false}
              actionName="View-Dashboard Detail"
            >
              <BurnTimeChart
                blockCode={blockCode}
                isRefreshed={isRefreshedBurnChart}
                blockDetails={blockDetail}
              />
            </HasPermission>

            <HasPermission
              moduleName="Dashboard Detail"
              isRoute={false}
              actionName="View-Dashboard Detail"
            >
              <EnergyConsumptionChart
                blockCode={blockCode}
                isRefreshed={isRefreshedEnergyConsumption}
                blockDetails={blockDetail}
              />
            </HasPermission>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardDetailsComponent;
